import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, API_BASE, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { successToast } from "../components/SuccessCheck";
import { useSettings, formatMoney } from "../lib/settings";
import { useAuth } from "../lib/auth";
import { Plus, Download, Link2 } from "lucide-react";
import { PaymentLinksModal } from "../components/PaymentLinksModal";
import { useStatusLabel } from "../components/StatusBadge";
import { useT, lbl, LANG } from "../lib/i18n";

import { tr } from "../lib/i18n";
export default function SalesPage() {
  const { settings } = useSettings();
  const { can, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const overdueOnly = params.get("overdue") === "1";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ vehicle_id: "", buyer_id: "", selling_price: "", discount_amount: 0, payment_method: "Cash", trade_in: null });
  const [tradein, setTradein] = useState(false);
  const [ti, setTi] = useState({ make: "", model: "", model_year: 2020, mileage: 0, value: 0 });
  const [belowMin, setBelowMin] = useState("");
  const [belowReason, setBelowReason] = useState("");
  const [reservedBy, setReservedBy] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const canDownload = user?.role !== "viewer";
  const [linkSale, setLinkSale] = useState(null);
  const statusLabel = useStatusLabel();
  const { t } = useT();
  const navigate = useNavigate();

  const reload = async () => {
    try {
      const url = overdueOnly ? "/sales/overdue" : "/sales";
      const { data } = await api.get(url);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { reload(); }, [overdueOnly]);
  useEffect(() => {
    if (!open) return;
    Promise.all(["Available", "Reserved"].map((st) => api.get(`/vehicles?status=${st}&limit=200`)))
      .then((rs) => setVehicles(rs.flatMap((r) => r.data.items || []))).catch(() => {});
    api.get("/customers?limit=200").then(({ data }) => setCustomers(data.items)).catch(() => {});
  }, [open]);

  const downloadInvoice = (id, reference) => {
    const token = localStorage.getItem("access_token");
    fetch(`${API_BASE}/sales/${id}/invoice?lang=${LANG}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${reference || "Invoice"}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const closeModal = () => { setOpen(false); setBelowMin(""); setBelowReason(""); setReservedBy(""); setOverrideReason(""); };

  const create = async () => {
    if (belowMin && !belowReason.trim()) { toast.error(tr("x.please_enter_a_reason_for_selling_below_")); return; }
    if (reservedBy && !overrideReason.trim()) { toast.error(tr("x.please_enter_a_reason_for_overriding_the")); return; }
    try {
      const payload = { ...form };
      payload.selling_price = Number(payload.selling_price || 0);
      payload.discount_amount = Number(payload.discount_amount || 0);
      if (belowMin) payload.below_min_reason = belowReason.trim();
      if (reservedBy) payload.override_reason = overrideReason.trim();
      if (tradein) payload.trade_in = { ...ti, value: Number(ti.value || 0), mileage: Number(ti.mileage || 0), model_year: Number(ti.model_year || 2020) };
      const { data } = await api.post("/sales", payload);
      successToast(tr("x.sale_created", { p0: data.reference }));
      closeModal();
      setForm({ vehicle_id: "", buyer_id: "", selling_price: "", discount_amount: 0, payment_method: "Cash", trade_in: null });
      setTradein(false);
      reload();
    } catch (e) {
      const msg = formatApiError(e);
      if (e?.response?.status === 422 && msg.includes("below_min_reason")) {
        setBelowMin(msg.replace(/\s*Provide below_min_reason to proceed\.?/, ""));
        return;
      }
      if (e?.response?.status === 409 && msg.includes("override_reason")) {
        setReservedBy(msg.replace(/\s*Provide override_reason to proceed\.?/, ""));
        return;
      }
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-5" data-testid="sales-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">{tr("x.transactions")}</div>
          <h1 className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>
            {overdueOnly ? tr("x.overdue_sales") : tr("x.sales")}
          </h1>
          {overdueOnly && <p className="text-xs text-red-600 mt-1">{tr("x.confirmed_sales_past_the_payment_terms_w")}</p>}
        </div>
        <div className="flex gap-2">
          <button data-testid="new-contract-btn" onClick={() => navigate("/forms/new/contract")} className="h-9 px-4 rounded-lg border text-sm font-semibold">{t("sf.new_contract")}</button>
          {overdueOnly ? (
            <button data-testid="sales-clear-overdue" onClick={() => setParams({})} className="h-9 px-4 rounded-lg border text-sm">{tr("x.show_all_sales")}</button>
          ) : null}
          {!overdueOnly && can("sales", "create") && (
            <button data-testid="add-sale-btn" onClick={() => setOpen(true)} className="h-9 px-4 rounded-lg bg-accent-brand text-white text-sm font-semibold flex items-center gap-2"><Plus size={14} /> {tr("x.new_sale")}</button>
          )}
        </div>
      </div>

      <div className="panel overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr><th className="whitespace-nowrap">{tr("x.reference")}</th><th>{tr("x.date")}</th><th>{tr("x.vehicle_2")}</th><th>{tr("x.buyer")}</th><th>{tr("x.salesperson")}</th>{!overdueOnly && <th>{tr("x.total")}</th>}<th>{overdueOnly ? tr("x.days_overdue") : tr("x.outstanding")}</th>{!overdueOnly && <th>{tr("x.status")}</th>}<th></th></tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} data-testid={`sale-row-${s.reference}`}>
                <td className="mono whitespace-nowrap">{s.reference}</td>
                <td className="text-xs">{s.sale_date}</td>
                <td className="text-xs">{s.vehicle_label}</td>
                <td className="text-xs">{s.buyer_name}</td>
                <td className="text-xs">{s.salesperson_name || "—"}</td>
                {!overdueOnly && <td className="tabular font-semibold whitespace-nowrap">{formatMoney(s.total, settings)}</td>}
                <td className={overdueOnly ? "tabular font-semibold text-red-600" : "tabular"}>
                  {overdueOnly ? tr("x.n_days", { p0: s.days_overdue }) : s.status === "Cancelled" ? "—" : formatMoney(s.outstanding, settings)}
                </td>
                {!overdueOnly && <td><span className={`pill ${s.status === "Fully Paid" ? "pill-available" : s.status === "Cancelled" ? "pill-returned" : "pill-reserved"}`}>{statusLabel(s.status)}</span></td>}
                <td>
                  {canDownload && (
                    <button
                      data-testid={`invoice-btn-${s.reference}`}
                      onClick={() => downloadInvoice(s.id, s.reference)}
                      className="text-xs text-accent-brand font-semibold inline-flex items-center gap-1 hover:underline"
                      title={tr("x.download_pdf_invoice")}
                    >
                      <Download size={12} /> {tr("x.invoice")}
                    </button>
                  )}
                  {can("payments", "create") && s.status !== "Cancelled" && Number(s.outstanding) > 0 && (
                    <button data-testid={`pay-link-btn-${s.reference}`} onClick={() => setLinkSale(s)}
                      className="text-xs text-accent-brand font-semibold inline-flex items-center gap-1 hover:underline ms-3 whitespace-nowrap">
                      <Link2 size={12} /> {t("pl.button")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={overdueOnly ? 7 : 9} className="text-center py-8 text-[var(--gray-400)]">{overdueOnly ? tr("x.no_overdue_sales") : tr("x.no_sales_yet")}</td></tr>}
          </tbody>
        </table>
      </div>

      {linkSale && <PaymentLinksModal sale={linkSale} onClose={() => setLinkSale(null)} />}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="panel p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sale-modal">
            <div className="eyebrow mb-3">{tr("x.new_sale")}</div>
            <div className="space-y-3">
              <select data-testid="sale-vehicle" value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm">
                <option value="">{tr("x.select_vehicle")}</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.stock_number} · {v.make} {v.model} {v.model_year}{v.status === "Reserved" ? tr("x.reserved_2") : ""}</option>)}
              </select>
              <select data-testid="sale-buyer" value={form.buyer_id} onChange={(e) => setForm({ ...form, buyer_id: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm">
                <option value="">{tr("x.select_buyer")}</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.mobile}</option>)}
              </select>
              <input data-testid="sale-price" type="number" placeholder={tr("x.selling_price")} step="0.001" value={form.selling_price} onChange={(e) => { setForm({ ...form, selling_price: e.target.value }); setBelowMin(""); }} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
              {belowMin && (
                <div className="rounded-lg border border-amber-400 bg-amber-50 p-3 space-y-2" data-testid="below-min-warning">
                  <div className="text-sm font-semibold text-amber-800">{belowMin.replace(/\.$/, "")}.</div>
                  <div className="text-xs text-amber-800">{tr("x.you_can_still_make_this_sale_please_give")}</div>
                  <textarea data-testid="below-min-reason" rows={2} placeholder={tr("x.reason_for_selling_below_the_minimum_pri")} value={belowReason} onChange={(e) => setBelowReason(e.target.value)} className="w-full rounded border px-3 py-2 text-sm" />
                </div>
              )}
              {reservedBy && (
                <div className="rounded-lg border border-amber-400 bg-amber-50 p-3 space-y-2" data-testid="reserved-override-warning">
                  <div className="text-sm font-semibold text-amber-800">{reservedBy.replace(/\.$/, "")}.</div>
                  <textarea data-testid="override-reason" rows={2} placeholder={tr("x.reason_for_overriding_the_reservation")} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} className="w-full rounded border px-3 py-2 text-sm" />
                </div>
              )}
              <input type="number" placeholder={tr("x.discount")} step="0.001" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
              <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm">
                {["Cash", "Bank Transfer", "Cheque", "Finance", "Mixed"].map((m) => <option key={m} value={m}>{lbl(m)}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={tradein} onChange={(e) => setTradein(e.target.checked)} /> {tr("x.include_trade_in")}</label>
              {tradein && (
                <div className="border border-[hsl(var(--border))] rounded-lg p-3 space-y-2">
                  <input placeholder={tr("x.trade_in_make")} value={ti.make} onChange={(e) => setTi({ ...ti, make: e.target.value })} className="w-full h-9 rounded border px-3 text-sm" />
                  <input placeholder={tr("x.trade_in_model")} value={ti.model} onChange={(e) => setTi({ ...ti, model: e.target.value })} className="w-full h-9 rounded border px-3 text-sm" />
                  <input type="number" placeholder={tr("x.trade_in_year")} value={ti.model_year} onChange={(e) => setTi({ ...ti, model_year: e.target.value })} className="w-full h-9 rounded border px-3 text-sm" />
                  <input type="number" placeholder={tr("x.mileage")} value={ti.mileage} onChange={(e) => setTi({ ...ti, mileage: e.target.value })} className="w-full h-9 rounded border px-3 text-sm" />
                  <input type="number" placeholder={tr("x.agreed_trade_in_value")} step="0.001" value={ti.value} onChange={(e) => setTi({ ...ti, value: e.target.value })} className="w-full h-9 rounded border px-3 text-sm" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={closeModal} className="h-9 px-4 rounded-lg border text-sm">{tr("x.cancel")}</button>
              <button data-testid="submit-sale" onClick={create} className="h-9 px-4 rounded-lg bg-accent-brand text-white text-sm font-semibold">{belowMin ? tr("x.create_sale_with_reason") : tr("x.create")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
